import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { useI18n } from '../../hooks/useI18n.js';
import { useAuth } from '../../hooks/useAuth.js';
import { unreadCount } from '../../state/notifications.js';
import { platformUpdatesCount, loadPlatformUpdatesCount, markAllUpdatesRead } from '../../state/platformUpdates.js';
import { toggleSidebar } from '../../state/ui.js';
import { api } from '../../state/auth.js';
import { route } from 'preact-router';
import LanguageSwitcher from '../shared/LanguageSwitcher.jsx';
import { parseServerDate } from '../../utils/dates.js';

const TYPE_META = {
  feature:     { label: 'Nieuw', color: '#16a34a', bg: '#dcfce7' },
  improvement: { label: 'Verbetering', color: '#d97706', bg: '#fef3c7' },
  bugfix:      { label: 'Bugfix', color: '#2563eb', bg: '#dbeafe' },
};

function UpdatesModal({ onClose }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api('/api/updates')
      .then(data => setUpdates(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => setLoading(false));

    // Mark all as read when modal opens
    markAllUpdatesRead();
  }, []);

  function formatDate(str) {
    return parseServerDate(str).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  return (
    <div
      style="position:fixed;inset:0;z-index:9999;display:flex;align-items:flex-start;justify-content:flex-end;padding:60px 16px 16px"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div style="position:fixed;inset:0;background:rgba(0,0,0,0.25)" onClick={onClose} />

      {/* Panel */}
      <div style="position:relative;background:var(--color-surface);border:1px solid var(--color-border);border-radius:16px;width:420px;max-height:80vh;display:flex;flex-direction:column;box-shadow:0 20px 60px rgba(0,0,0,0.15);z-index:1">
        {/* Header */}
        <div style="padding:20px 20px 16px;border-bottom:1px solid var(--color-border);display:flex;align-items:center;justify-content:space-between">
          <div>
            <h2 style="font-size:17px;font-weight:700;margin:0 0 2px">Wat is er nieuw? 🚀</h2>
            <p style="font-size:12px;color:var(--color-text-muted);margin:0">De laatste updates van het platform</p>
          </div>
          <button
            onClick={onClose}
            style="background:none;border:none;cursor:pointer;color:var(--color-text-muted);padding:4px;border-radius:6px;line-height:1"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
        </div>

        {/* Content */}
        <div style="overflow-y:auto;flex:1;padding:16px 20px;display:flex;flex-direction:column;gap:16px">
          {loading ? (
            [0,1,2].map(i => (
              <div key={i} class="skeleton" style="height:80px;border-radius:10px" />
            ))
          ) : updates.length === 0 ? (
            <div style="text-align:center;padding:40px 0;color:var(--color-text-muted)">
              <div style="font-size:32px;margin-bottom:8px">🎉</div>
              <p style="font-size:14px;margin:0">Nog geen updates gepubliceerd.</p>
            </div>
          ) : (
            updates.map(u => {
              const meta = TYPE_META[u.update_type] || TYPE_META.feature;
              return (
                <div
                  key={u.id}
                  style={`border-radius:10px;border:1px solid var(--color-border);padding:14px;${!u.is_read ? 'background:var(--color-bg-secondary)' : ''}`}
                >
                  <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;flex-wrap:wrap">
                    <span style={`font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;background:${meta.bg};color:${meta.color}`}>
                      {meta.label}
                    </span>
                    {u.version && (
                      <span style="font-size:11px;background:var(--color-border);color:var(--color-text-muted);padding:2px 8px;border-radius:10px;font-family:monospace">
                        v{u.version}
                      </span>
                    )}
                    {!u.is_read && (
                      <span style="width:7px;height:7px;border-radius:50%;background:var(--color-primary);display:inline-block;flex-shrink:0" title="Nieuw" />
                    )}
                    <span style="font-size:11px;color:var(--color-text-muted);margin-left:auto">{formatDate(u.created_at)}</span>
                  </div>
                  <div style="font-weight:600;font-size:14px;margin-bottom:6px">{u.title}</div>
                  <div style="font-size:13px;color:var(--color-text-muted);line-height:1.6;white-space:pre-wrap">{u.content}</div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const { t } = useI18n();
  const auth = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showUpdates, setShowUpdates] = useState(false);

  // Load unread count on mount
  useEffect(() => {
    loadPlatformUpdatesCount();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      route(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleOpenUpdates = () => {
    setShowUpdates(true);
  };

  return (
    <>
      <header class="app-header">
        <button class="header-menu-btn" onClick={toggleSidebar}>
          <svg width="24" height="24" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
          </svg>
        </button>

        {/* Searches what the member may open: tasks, processes, documents, members, meetings */}
        {auth.activeClubId && (
          <form class="header-search" onSubmit={handleSearch} role="search">
            <svg viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
            </svg>
            <input
              type="text"
              placeholder={t('common.search')}
              aria-label="Zoeken in de club"
              value={searchQuery}
              onInput={(e) => setSearchQuery(e.target.value)}
            />
          </form>
        )}

        <div class="header-actions">
          <LanguageSwitcher />

          {/* Platform updates button — visible for all users */}
          <button
            class="notification-bell"
            onClick={handleOpenUpdates}
            title="Wat is er nieuw?"
            style="position:relative"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"/>
            </svg>
            {platformUpdatesCount.value > 0 && (
              <span class="notification-badge">
                {platformUpdatesCount.value > 9 ? '9+' : platformUpdatesCount.value}
              </span>
            )}
          </button>

          {!auth.isPlatformAdmin && (
            <button class="notification-bell" onClick={() => route('/notifications')}>
              <svg width="22" height="22" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zm0 16a3 3 0 01-3-3h6a3 3 0 01-3 3z"/>
              </svg>
              {unreadCount.value > 0 && (
                <span class="notification-badge">{unreadCount.value > 99 ? '99+' : unreadCount.value}</span>
              )}
            </button>
          )}

          <div
            class="header-avatar"
            onClick={() => route('/profile')}
            title="Mijn profiel"
            style="cursor:pointer;overflow:hidden;padding:0"
          >
            {auth.avatarUrl
              ? <img src={auth.avatarUrl} alt="" style="width:100%;height:100%;object-fit:cover;border-radius:50%;display:block" />
              : (auth.displayName || 'U')[0]
            }
          </div>
        </div>
      </header>

      {showUpdates && <UpdatesModal onClose={() => setShowUpdates(false)} />}
    </>
  );
}
